import { User } from "firebase/auth";
import { addDoc, collection } from "firebase/firestore";
import algoliasearch, { SearchIndex } from "algoliasearch/lite";
import { createSearchParams, ParamKeyValuePair } from "react-router";
import { Coordinates } from "@/models";
import SearchLog, { SearchLogData, searchLogDataConverter } from "../../../components/SearchForm/converter";
import RemoteConfigService from "@/remoteConfig";
import { getFirestoreDB } from "@/firebase";

export type SearchParams = {
    condition: string;
    studyTypes: string[];
    coordinates: Coordinates;
    age: string;
    gender: string;
    distanceInMeters: number | "all";
    subset?: string;
    locationDisplayName?: string;
    page?: number;
};

/**
 * Turns a url search query object into a SearchParams object, only populating specified fields.
 * @param search a URLSearchParam object
 * @returns
 */
export function searchParamsToObject(search: URLSearchParams): Partial<SearchParams> {
    const params: Partial<SearchParams> = {};
    if (search.has("condition")) params.condition = search.get("condition")!;
    if (search.has("age")) params.age = search.get("age")!;
    if (search.has("gender")) params.gender = search.get("gender")!;
    if (search.has("lat") && search.has("lng"))
        params.coordinates = {
            lat: Number(search.get("lat")),
            lng: Number(search.get("lng")),
        };
    if (search.has("distanceInMeters")) {
        const distanceInMeters = search.get("distanceInMeters")!;
        params.distanceInMeters = distanceInMeters === "all" ? "all" : Number(distanceInMeters);
    }
    if (search.has("locationDisplayName")) {
        params.locationDisplayName = search.get("locationDisplayName")!;
    }

    if (search.has("studyType")) params.studyTypes = search.getAll("studyType");
    if (search.has("page")) params.page = Number(search.get("page"));
    if (search.has("subset")) params.subset = search.get("subset")!;
    return params;
}

/**
 * Turns a partial SearchParams object into a search query string, only populating with explicitly defined fields
 * @param search
 */
export function objectToSearchParams(search: Partial<SearchParams>): URLSearchParams {
    const { condition, age, gender, distanceInMeters, locationDisplayName, studyTypes, coordinates, subset, page } =
        search;

    const paramsArray: ParamKeyValuePair[] = [];
    if (condition && condition.length > 0) paramsArray.push(["condition", condition]);
    if (age && age.length > 0) paramsArray.push(["age", age]);
    if (gender && gender.length > 0) paramsArray.push(["gender", gender]);
    if (distanceInMeters) paramsArray.push(["distanceInMeters", distanceInMeters.toString()]);
    if (locationDisplayName) paramsArray.push(["locationDisplayName", locationDisplayName]);
    if (studyTypes && studyTypes.length > 0) studyTypes.forEach((type) => paramsArray.push(["studyType", type]));
    if (coordinates) {
        paramsArray.push(["lat", coordinates.lat.toString()]);
        paramsArray.push(["lng", coordinates.lng.toString()]);
    }
    if (page) {
        paramsArray.push(["page", page.toString()]);
    }
    if (subset) paramsArray.push(["subset", subset]);
    const urlParams = createSearchParams(paramsArray);
    return urlParams;
}

/**
 * Logs an attempted search into the search log database using the provided fields.
 *
 * @param searchParams The parameters of the search
 * @param userProfile Optional. The user to associate with the search.
 * @returns The Document ID of the search log.
 * @throws An error if the search log is unable to be created
 */
async function logSearchAttempt(
    searchParams: Partial<SearchLogData>,
    uid?: string,
    userOrg?: string,
    isAnonymous?: boolean
): Promise<string> {
    const db = getFirestoreDB();
    console.debug(`Search attempted with following values ${JSON.stringify(searchParams)}`);

    const getCookieValue = (name: string): string | null => {
        const cookies = document.cookie.split("; ");
        const cookie = cookies.find((c) => c.startsWith(`${name}=`));
        return cookie ? cookie.split("=")[1] : null;
    };

    const cookieValue = getCookieValue("_ga") || "";

    const searchLog = new SearchLog({
        uid: uid || "",
        isAnonymous: isAnonymous !== undefined ? isAnonymous : true,
        userOrg,
        location: searchParams.location,
        condition: searchParams.condition,
        studyTypes: searchParams.studyTypes,
        distance: searchParams.distance,
        age: searchParams.age,
        gender: searchParams.gender,
        createdAt: searchParams.createdAt || new Date(),
        sessionId: cookieValue,
    });
    try {
        const searchCollectionRef = collection(db, "trialFinderSearchLog").withConverter(searchLogDataConverter);
        const docRef = await addDoc(searchCollectionRef, searchLog);
        return docRef.id;
    } catch (error) {
        console.error("Error storing values in Firestore: ", error);
        throw new Error("Search log could not be created");
    }
}

const createFacetArray = (
    studyTypes?: SearchParams["studyTypes"],
    age?: SearchParams["age"],
    gender?: SearchParams["gender"]
): string[][] => {
    const facetArray: string[][] = [];
    if (studyTypes) {
        facetArray.push(studyTypes.map((type) => `o.options.${type}: ${true}`));
    }
    if (age) facetArray.push([`o.eligibility.ageCat : ${age}`]);
    // When the user selects a gendered option, Algolia includes studies labeled “MALE” and “ALL” OR “FEMALE and “ALL”
    if (gender) {
        facetArray.push([`o.eligibility.sex : ${gender}`, "o.eligibility.sex : ALL"]);
    }

    facetArray.push(["o.overallStatus: RECRUITING", "o.overallStatus: NOT_YET_RECRUITING"]);

    return facetArray;
};

const TRIAL_FINDER_INDEX = "trialFinderStudy";
let algoliaIndex: SearchIndex;
export const HITS_PER_PAGE = 10;
/**
 *  Searches algolia index trialFinderStudy with specified parameters
 *
 * @param params Search parameters to use. Handles default values if not provided
 * @param user The User to associate the created search log with.
 * @param page Zero-index page of results. Will throw error if negative
 * @returns Hits, total number of hits, and search log ID
 */
const searchAlgolia = async (
    params: Partial<SearchParams>,
    user?: User | { uid: string; isAnonymous: boolean } | null,
    userOrg?: string,
    page = 0
) => {
    if (page < 0) {
        throw Error("Page number is negative.");
    }

    if (algoliaIndex === undefined) {
        const searchClient = await RemoteConfigService.Instance().then((instance) =>
            algoliasearch(instance.algoliaApplicationId, instance.algoliaAPIKey)
        );
        algoliaIndex = searchClient.initIndex(TRIAL_FINDER_INDEX);
    }

    const { condition, coordinates, age, gender, distanceInMeters, studyTypes, subset } = params;

    const facetArray: string[][] = createFacetArray(studyTypes, age, gender);

    if (userOrg) {
        // We don't have any organization-specific facets yet, but if we did, we would add them here.
        // facetArray.push([`organization: ${userOrg}`]);
    }

    const searchLogId = await logSearchAttempt(
        {
            location: coordinates,
            condition,
            studyTypes,
            distance: distanceInMeters,
            age,
            gender,
        },
        user?.uid,
        userOrg,
        user?.isAnonymous
    ).catch((error) => {
        console.error(error);
        return undefined;
    });
    console.debug("Search Log ID: ", searchLogId);
    console.log(subset);

    // IMPORTANT: The Search function only returns up to 1000 records. If you want to retrieve more, you must set the
    // paginationLimitedTo param higher, or use the "browse" function.
    // ranking info generated only if getRankingInfo is set to true, use this to get the primary location for each study
    return algoliaIndex
        .search(condition || "", {
            getRankingInfo: true,
            clickAnalytics: true,
            page,
            aroundLatLng: coordinates ? `${coordinates.lat}, ${coordinates.lng}` : undefined,
            aroundRadius: distanceInMeters,
            facetFilters: facetArray,
            hitsPerPage: HITS_PER_PAGE,
            ruleContexts: [subset || ""],
        })
        .then((searchResponse) => ({ ...searchResponse, searchLogId }));
};

export default searchAlgolia;
